import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { FormContainer } from "../../customer/customerManagement/styles";
import { useTranslation } from "react-i18next";
import { Heading1 } from "../../../common/components/styled/styled";
import { Box } from "@mui/material";
import SearchField from "../../../common/components/functional/SearchField";
import { StripedTable } from "../../../common/components/functional/stripedTable/StripedTable";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { useCallback, useEffect, useState } from "react";
import { Talent } from "../../talents/talentAPI";
import { TalentTableRow } from "./TalentTableRow";
import { fetchTalentsAutocomplete } from "../../talents/talentsSlice";
import { isFulfilled } from "@reduxjs/toolkit";
import { TempPasswordDialog } from "../../../common/components/functional/tempPasswordDialog/TempPasswordDialog";
import {
  setTalentTemporaryPassword,
  updateTalentStatus,
} from "../settingsActions";
import { getName } from "../../../common/helpers/common";
import { EditEmailDialog } from "./EditEmailDialog";
import { TalentsActionMenu, MenuItemKey } from "./TalentsActionMenu";
import { TalentStatusDialog } from "./TalentStatusDialog";
import { TalentStatus } from "../../talents/types/common";
import { TalentStatusUpdated } from "../../talents/types/events";
import { EventPayload } from "../../../common/types/Event";
import { RedactionPlanDialog } from "./RedactionPlanDialog";

type MenuState = {
  anchorEl: HTMLButtonElement | null;
  talent: Talent | null;
};

export const TalentsControlPanel = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { status } = useAppSelector((state) => state.talents);
  const isLoading = status === "pending";

  const [searchString, setSearchString] = useState<string>("");
  const [debouncedSearchString, setDebouncedSearchString] = useState("");

  const [allTalents, setAllTalents] = useState<Talent[]>([]);

  const [selectedTalent, setSelectedTalent] = useState<Talent | undefined>();
  const [isPasswordialogOpen, setIsPasswordialogOpen] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isRedactionPlanDialogOpen, setIsRedactionPlanDialogOpen] =
    useState(false);
  const [selectedStatus, setSelectedStatus] = useState<TalentStatus>(
    TalentStatus.NEW,
  );
  const [menuState, setMenuState] = useState<MenuState>({
    anchorEl: null,
    talent: null,
  });

  const onMenuOpen = (anchorEl: HTMLButtonElement, talent: Talent) => {
    setMenuState({ anchorEl, talent });
  };

  const onMenuClose = () =>
    setMenuState({ ...menuState, anchorEl: null, talent: null });

  const rows = allTalents
    .sort((a, b) => a.account.lastName.localeCompare(b.account.lastName))
    .map((talent) => (
      <TalentTableRow key={talent.id} talent={talent} onMenuOpen={onMenuOpen} />
    ));

  const dispatchSearch = useCallback(
    async (search: string) => {
      const searchResults = await dispatch(
        fetchTalentsAutocomplete({ search: search, pageSize: "100" }),
      );

      if (isFulfilled(searchResults)) {
        setAllTalents(searchResults.payload.data as Talent[]);
      }
    },
    [dispatch],
  );

  const closeDialog = () => {
    setIsPasswordialogOpen(false);
    setIsEmailDialogOpen(false);
    setIsStatusDialogOpen(false);
    setIsRedactionPlanDialogOpen(false);
    setSelectedTalent(undefined);
  };

  const onSubmit = () => {
    if (!selectedTalent) {
      return Promise.resolve();
    }

    return dispatch(
      setTalentTemporaryPassword({ talentId: selectedTalent.id }),
    );
  };

  const handleEmailUpdated = (updatedEmail: string) => {
    if (!selectedTalent) {
      return;
    }

    setAllTalents((prev) =>
      prev.map((talent) =>
        talent.id === selectedTalent.id
          ? {
              ...talent,
              account: {
                ...talent.account,
                emailAddress: updatedEmail,
              },
            }
          : talent,
      ),
    );
  };

  const updateLocalTalentStatus = (
    talentId: string,
    status: TalentStatus,
    version: number,
  ) => {
    setAllTalents((prev) =>
      prev.map((talent) =>
        talent.id === talentId
          ? {
              ...talent,
              account: {
                ...talent.account,
                status,
                version,
              },
            }
          : talent,
      ),
    );
  };

  const getLatestEventVersion = (
    payload: EventPayload<TalentStatusUpdated>[],
    fallbackVersion: number,
  ) => {
    if (!payload.length) {
      return fallbackVersion;
    }

    return payload[payload.length - 1].version;
  };

  const handleDisableTalent = async (talent: Talent) => {
    const action = await dispatch(
      updateTalentStatus({
        talentId: talent.id,
        status: TalentStatus.DISABLED,
        version: talent.account.version,
      }),
    );

    if (isFulfilled(action)) {
      const version = getLatestEventVersion(
        action.payload as EventPayload<TalentStatusUpdated>[],
        talent.account.version,
      );
      updateLocalTalentStatus(talent.id, TalentStatus.DISABLED, version);
    }
  };

  const handleEnableTalent = async (talent: Talent, status: TalentStatus) => {
    const action = await dispatch(
      updateTalentStatus({
        talentId: talent.id,
        status,
        version: talent.account.version,
      }),
    );

    if (isFulfilled(action)) {
      const version = getLatestEventVersion(
        action.payload as EventPayload<TalentStatusUpdated>[],
        talent.account.version,
      );
      updateLocalTalentStatus(talent.id, status, version);
    }
  };

  const menuActions = {
    [MenuItemKey.EDIT_EMAIL]: (talent: Talent | null) => {
      if (!talent) {
        return;
      }

      setSelectedTalent(talent);
      setIsEmailDialogOpen(true);
    },
    [MenuItemKey.RESET_PASSWORD]: (talent: Talent | null) => {
      if (!talent) {
        return;
      }

      setSelectedTalent(talent);
      setIsPasswordialogOpen(true);
    },
    [MenuItemKey.REDACTION_PLAN]: (talent: Talent | null) => {
      if (!talent) {
        return;
      }

      setSelectedTalent(talent);
      setIsRedactionPlanDialogOpen(true);
    },
    [MenuItemKey.DISABLE]: (talent: Talent | null) => {
      if (!talent) {
        return;
      }

      handleDisableTalent(talent);
    },
    [MenuItemKey.ENABLE]: (talent: Talent | null) => {
      if (!talent) {
        return;
      }

      setSelectedTalent(talent);
      setSelectedStatus(TalentStatus.NEW);
      setIsStatusDialogOpen(true);
    },
  };

  useEffect(() => {
    dispatchSearch(debouncedSearchString);
  }, [dispatch, dispatchSearch, debouncedSearchString]);

  const onSearchInputChange = (value: string) => setSearchString(value);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setDebouncedSearchString(searchString);
    }, 800);

    return () => {
      clearTimeout(debounce);
    };
  }, [searchString]);

  return (
    <>
      <OverlaySpinner isLoading={isLoading} />
      <FormContainer>
        <Heading1>{t("talents")}</Heading1>
        <>
          <Box width="600px">
            <SearchField
              label={t("search") || ""}
              value={searchString}
              onChange={(value) => onSearchInputChange(value)}
              sx={{ marginBottom: 2 }}
            />
          </Box>
          <StripedTable
            columnHeaders={[
              t("common:name"),
              t("common:email"),
              t("talentProfile:phoneNumber"),
              t("settings:enabled"),
              "",
            ]}
            itemRows={rows}
            disableRowClick={true}
          />
        </>
      </FormContainer>
      {selectedTalent && (
        <TempPasswordDialog
          isOpen={isPasswordialogOpen}
          userName={getName(selectedTalent.account, "firstNameFirst")}
          userEmail={selectedTalent.account.emailAddress}
          onSetTempPassword={onSubmit}
          close={closeDialog}
        />
      )}
      {selectedTalent && (
        <EditEmailDialog
          talentId={selectedTalent.id}
          currentEmail={selectedTalent.account.emailAddress}
          isOpen={isEmailDialogOpen}
          onClose={closeDialog}
          onEmailUpdated={handleEmailUpdated}
        />
      )}
      {selectedTalent && (
        <TalentStatusDialog
          isOpen={isStatusDialogOpen}
          status={selectedStatus}
          onClose={closeDialog}
          onStatusChange={setSelectedStatus}
          onConfirm={() => {
            if (!selectedTalent) {
              return;
            }

            handleEnableTalent(selectedTalent, selectedStatus);
            closeDialog();
          }}
        />
      )}
      <TalentsActionMenu
        open={!!menuState.anchorEl}
        anchorEl={menuState.anchorEl}
        onClose={onMenuClose}
        talent={menuState.talent}
        onExited={onMenuClose}
        menuActions={menuActions}
      />
      {selectedTalent && (
        <RedactionPlanDialog
          isOpen={isRedactionPlanDialogOpen}
          talentId={selectedTalent.id}
          onClose={closeDialog}
        />
      )}
    </>
  );
};
